import { type ReactNode, useMemo, useState } from "react";
import { CalendarDays, CopyPlus, Edit3, FileClock, Plus, RefreshCw, ShieldAlert, Sparkles, Trash2 } from "lucide-react";
import {
  createCalidadOportunidad,
  createCalidadRiesgo,
  createRiesgosOportunidadesControlModificacion,
  crearPlantillaAnual,
  deleteCalidadOportunidad,
  deleteCalidadRiesgo,
  deleteRiesgosOportunidadesYear,
  deleteRiesgosOportunidadesControlModificacion,
  updateCalidadOportunidad,
  updateCalidadRiesgo,
  updateRiesgosOportunidadesControlModificacion,
} from "../api/riesgos-oportunidades";
import { useCalidadRiesgosOportunidades } from "../hooks/useCalidadRiesgosOportunidades";
import { CalidadControlModificacionesTable } from "../../../components/control-modificaciones/CalidadControlModificacionesTable";
import { Iso900114001ConfirmDialog } from "../../../components/shared/Iso900114001ConfirmDialog";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import type { CalidadControlModificacionPayload } from "../../../types/control-modificaciones";
import {
  createEmptyOportunidadForm,
  createEmptyRiesgoForm,
  defaultRiesgosOportunidadesFilters,
  mapOportunidadToPayload,
  mapRiesgoToPayload,
  type CalidadOportunidad,
  type CalidadOportunidadPayload,
  type CalidadRiesgo,
  type CalidadRiesgoPayload,
} from "../types/riesgos-oportunidades";

type Props = { onBack: () => void };
type Tab = "riesgos" | "oportunidades" | "control";
type FormState =
  | { open: false }
  | { open: true; tipo: "riesgo"; mode: "create" | "edit"; row: CalidadRiesgoPayload; id?: number }
  | { open: true; tipo: "oportunidad"; mode: "create" | "edit"; row: CalidadOportunidadPayload; id?: number };
type ControlState =
  | { open: false }
  | { open: true; mode: "create" | "edit"; row: CalidadControlModificacionPayload; id?: number };

type DeleteState =
  | { open: false }
  | { open: true; title: string; description: string; confirmLabel: string; action: () => Promise<void> };

export function CalidadRiesgosOportunidadesSection({ onBack }: Props) {
  const [tab, setTab] = useState<Tab>("riesgos");
  const [filters, setFilters] = useState(defaultRiesgosOportunidadesFilters);
  const [formState, setFormState] = useState<FormState>({ open: false });
  const [controlState, setControlState] = useState<ControlState>({ open: false });
  const [templateOpen, setTemplateOpen] = useState(false);
  const [deleteState, setDeleteState] = useState<DeleteState>({ open: false });
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { riesgos, oportunidades, control_modificaciones, meta, loading, error: loadError, reload } = useCalidadRiesgosOportunidades(filters);

  const activeYear = useMemo(() => {
    if (filters.anio !== "" && Number.isFinite(Number(filters.anio))) return Number(filters.anio);
    return meta.default_source_year ?? meta.current_year;
  }, [filters.anio, meta.current_year, meta.default_source_year]);

  const yearLabel = filters.anio === "" ? "Todos" : filters.anio;

  const openNewRiesgo = () => setFormState({ open: true, tipo: "riesgo", mode: "create", row: createEmptyRiesgoForm(activeYear) });
  const openNewOportunidad = () =>
    setFormState({ open: true, tipo: "oportunidad", mode: "create", row: createEmptyOportunidadForm(activeYear) });

  const handleSubmitForm = async () => {
    if (!formState.open) return;
    setSubmitting(true);
    setError(null);
    try {
      if (formState.tipo === "riesgo") {
        if (formState.mode === "create") await createCalidadRiesgo(formState.row);
        else if (formState.id) await updateCalidadRiesgo(formState.id, formState.row);
      } else {
        if (formState.mode === "create") await createCalidadOportunidad(formState.row);
        else if (formState.id) await updateCalidadOportunidad(formState.id, formState.row);
      }
      setFormState({ open: false });
      await reload();
    } catch {
      setError("No se pudo guardar el registro.");
    } finally {
      setSubmitting(false);
    }
  };


  const handleConfirmDelete = async () => {
    if (!deleteState.open) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteState.action();
      setDeleteState({ open: false });
      await reload();
    } catch {
      setError("No se pudo completar la eliminación.");
    } finally {
      setDeleting(false);
    }
  };

  const requestDeleteYear = () => {
    if (filters.anio === "" || !Number.isFinite(Number(filters.anio))) return;
    const year = Number(filters.anio);
    setDeleteState({
      open: true,
      title: `Eliminar datos del año ${year}`,
      description: `Se eliminarán todos los riesgos y oportunidades del año ${year}. El control documental se mantendrá intacto.`,
      confirmLabel: "Eliminar año",
      action: async () => {
        await deleteRiesgosOportunidadesYear(year);
        setFilters((current) => ({ ...current, anio: "" }));
      },
    });
  };

  const handleSubmitControl = async () => {
    if (!controlState.open) return;
    setSubmitting(true);
    setError(null);
    try {
      if (controlState.mode === "create") await createRiesgosOportunidadesControlModificacion(controlState.row);
      else if (controlState.id) await updateRiesgosOportunidadesControlModificacion(controlState.id, controlState.row);
      setControlState({ open: false });
      await reload();
    } catch {
      setError("No se pudo guardar el control documental.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="cro-section">
      <Iso900114001SectionHeader
        eyebrow="Evaluación anual"
        title="Riesgos y oportunidades"
        description="Documento anual editable, con copia desde el año anterior y control documental."
        onBack={onBack}
        onRefresh={() => {
          void reload();
        }}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid" aria-label="Resumen de registros">
        <Iso900114001OverviewCard icon={ShieldAlert} label="Riesgos" value={loading ? "..." : meta.riesgos_total} />
        <Iso900114001OverviewCard icon={Sparkles} label="Oportunidades" value={loading ? "..." : meta.oportunidades_total} />
        <Iso900114001OverviewCard icon={CalendarDays} label="Vista" value={yearLabel} />
        <Iso900114001OverviewCard icon={FileClock} label="Control documental" value={loading ? "..." : control_modificaciones.length} />
      </div>

      <Iso900114001Tabs
        tabs={[
          { id: "riesgos", label: "Riesgos", count: meta.riesgos_total },
          { id: "oportunidades", label: "Oportunidades", count: meta.oportunidades_total },
          {
            id: "control",
            label: "Control documental",
            count: control_modificaciones.length,
          },
        ]}
        activeTab={tab}
        onChange={setTab}
        ariaLabel="Pestañas de riesgos y oportunidades"
      />

      <div className="cro-panel cro-filters-panel">
        <div className="cro-filter-field">
          <span>Filtro año</span>
          <select value={filters.anio} onChange={(event) => setFilters((current) => ({ ...current, anio: event.target.value }))}>
            <option value="">Todos</option>
            {meta.years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="cro-filter-field cro-filter-search">
          <span>Buscar</span>
          <input
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            placeholder="Origen, nº, responsable, texto..."
          />
        </div>
        <div className="cro-filter-actions">
          <button type="button" className="cro-button cro-button-muted" onClick={() => void reload()}>
            <RefreshCw size={16} /> Recargar
          </button>
          <button type="button" className="cro-button cro-button-danger" disabled={filters.anio === ""} onClick={requestDeleteYear}>
            <Trash2 size={16} /> Eliminar año
          </button>
          <button type="button" className="cro-button cro-button-primary" onClick={() => setTemplateOpen(true)}>
            <CopyPlus size={16} /> Crear plantilla anual
          </button>
        </div>
      </div>

      {loadError ? <div className="cro-alert">{loadError}</div> : null}
      {error ? <div className="cro-alert">{error}</div> : null}

      {tab === "riesgos" ? (
        <RiesgosTable
          rows={riesgos}
          loading={loading}
          onCreate={openNewRiesgo}
          onEdit={(row) => setFormState({ open: true, tipo: "riesgo", mode: "edit", row: mapRiesgoToPayload(row), id: row.id })}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              title: "Eliminar riesgo",
              description: `Se eliminará el riesgo ${row.numero} del año ${row.anio}.`,
              confirmLabel: "Eliminar riesgo",
              action: async () => {
                await deleteCalidadRiesgo(row.id);
              },
            })
          }
        />
      ) : tab === "oportunidades" ? (
        <OportunidadesTable
          rows={oportunidades}
          loading={loading}
          onCreate={openNewOportunidad}
          onEdit={(row) => setFormState({ open: true, tipo: "oportunidad", mode: "edit", row: mapOportunidadToPayload(row), id: row.id })}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              title: "Eliminar oportunidad",
              description: `Se eliminará la oportunidad ${row.numero} del año ${row.anio}.`,
              confirmLabel: "Eliminar oportunidad",
              action: async () => {
                await deleteCalidadOportunidad(row.id);
              },
            })
          }
        />
      ) : (
        <CalidadControlModificacionesTable
          rows={control_modificaciones}
          loading={loading}
          error={loadError}
          onRetry={() => {
            void reload();
          }}
          onCreate={() => setControlState({ open: true, mode: "create", row: { fecha: "", modificaciones_realizadas: "" } })}
          onEdit={(row) => setControlState({ open: true, mode: "edit", id: row.id, row: { fecha: row.fecha, modificaciones_realizadas: row.modificaciones_realizadas } })}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              title: "Eliminar modificación",
              description: `Se eliminará la modificación registrada en ${row.fecha}.`,
              confirmLabel: "Eliminar modificación",
              action: async () => {
                await deleteRiesgosOportunidadesControlModificacion(row.id);
              },
            })
          }
        />
      )}

      {formState.open ? <RegistroModal state={formState} setState={setFormState} submitting={submitting} onSubmit={handleSubmitForm} /> : null}
      {controlState.open ? <ControlModal state={controlState} setState={setControlState} submitting={submitting} onSubmit={handleSubmitControl} /> : null}
      <Iso900114001ConfirmDialog
        open={deleteState.open}
        title={deleteState.open ? deleteState.title : "Eliminar registro"}
        description={deleteState.open ? deleteState.description : ""}
        confirmLabel={deleteState.open ? deleteState.confirmLabel : "Eliminar"}
        confirming={deleting}
        onClose={() => setDeleteState({ open: false })}
        onConfirm={handleConfirmDelete}
      />
      {templateOpen ? (
        <PlantillaModal
          years={meta.years}
          sourceYear={meta.default_source_year}
          targetYear={meta.suggested_new_year}
          submitting={submitting}
          onClose={() => setTemplateOpen(false)}
          onSubmit={async (payload) => {
            setSubmitting(true);
            setError(null);
            try {
              await crearPlantillaAnual(payload);
              setTemplateOpen(false);
              setFilters((current) => ({ ...current, anio: String(payload.target_year) }));
              await reload();
            } catch {
              setError("No se pudo crear la plantilla anual. Revisa que el año destino no exista ya.");
            } finally {
              setSubmitting(false);
            }
          }}
        />
      ) : null}
    </section>
  );
}


function EmptyOrLoading({ loading, colSpan, emptyText }: { loading?: boolean; colSpan: number; emptyText: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="cro-empty-cell">{loading ? "Cargando..." : emptyText}</td>
    </tr>
  );
}

function ScrollableTableValue({ children, emphasis = false }: { children: ReactNode; emphasis?: boolean }) {
  return (
    <div className="cro-cell-scroll">
      {emphasis ? <strong>{children}</strong> : children}
    </div>
  );
}

function RiesgosTable({ rows, loading, onCreate, onEdit, onDelete }: { rows: CalidadRiesgo[]; loading: boolean; onCreate: () => void; onEdit: (row: CalidadRiesgo) => void; onDelete: (row: CalidadRiesgo) => void }) {
  return (
    <div className="cro-panel cro-card">
      <div className="cro-card-head">
        <div>
          <p className="cro-card-kicker">Listado anual</p>
          <h2>Riesgos</h2>
        </div>
        <div className="cro-card-actions">
          <span className="cro-count-badge">{rows.length} registros</span>
          <button type="button" className="cro-button cro-button-primary" onClick={onCreate}><Plus size={16} /> Añadir riesgo</button>
        </div>
      </div>
      <div className="cro-table-scroll">
        <table className="cro-table cro-risk-table">
          <thead>
            <tr className="cro-group-header">
              <th rowSpan={3} className="cro-actions-col">Acciones</th>
              <th rowSpan={3}>Año</th>
              <th rowSpan={3}>Nº</th>
              <th rowSpan={3}>Origen</th>
              <th rowSpan={3}>Cuestiones</th>
              <th rowSpan={3}>Riesgos</th>
              <th rowSpan={3}>Procesos afectados</th>
              <th rowSpan={3}>Decisión de la dirección</th>
              <th colSpan={7}>Planificación de acciones</th>
            </tr>
            <tr className="cro-group-header cro-subgroup-header">
              <th rowSpan={2}>Controles/acciones a implantar</th>
              <th rowSpan={2}>Responsable</th>
              <th rowSpan={2}>Plazos</th>
              <th rowSpan={2} className="cro-separator-right">Recursos</th>
              <th colSpan={3} className="cro-efficacy-group">Eficacia</th>
            </tr>
            <tr className="cro-column-header">
              <th className="cro-separator-left">1er semestre</th>
              <th>2º semestre</th>
              <th>Resultado de la eficacia</th>
            </tr>
          </thead>
          <tbody>
            {loading || rows.length === 0 ? <EmptyOrLoading loading={loading} colSpan={15} emptyText="No hay riesgos registrados." /> : rows.map((row) => (
              <tr key={row.id}>
                <td className="cro-row-actions">
                  <div className="cro-row-actions-inner">
                    <button type="button" title="Editar" onClick={() => onEdit(row)}><Edit3 size={15} /></button>
                    <button type="button" title="Eliminar" onClick={() => onDelete(row)}><Trash2 size={15} /></button>
                  </div>
                </td>
                <td><ScrollableTableValue>{row.anio}</ScrollableTableValue></td>
                <td><ScrollableTableValue emphasis>{row.numero}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.origen}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.cuestiones}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.riesgos}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.procesos_afectados}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.decision_direccion}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.controles_acciones_implantar}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.responsable}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.plazos}</ScrollableTableValue></td>
                <td className="cro-separator-right"><ScrollableTableValue>{row.recursos}</ScrollableTableValue></td>
                <td className="cro-separator-left"><ScrollableTableValue>{row.eficacia_primer_semestre}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.eficacia_segundo_semestre}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.resultado_eficacia}</ScrollableTableValue></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OportunidadesTable({ rows, loading, onCreate, onEdit, onDelete }: { rows: CalidadOportunidad[]; loading: boolean; onCreate: () => void; onEdit: (row: CalidadOportunidad) => void; onDelete: (row: CalidadOportunidad) => void }) {
  return (
    <div className="cro-panel cro-card">
      <div className="cro-card-head">
        <div>
          <p className="cro-card-kicker">Listado anual</p>
          <h2>Oportunidades</h2>
        </div>
        <div className="cro-card-actions">
          <span className="cro-count-badge">{rows.length} registros</span>
          <button type="button" className="cro-button cro-button-primary" onClick={onCreate}><Plus size={16} /> Añadir oportunidad</button>
        </div>
      </div>
      <div className="cro-table-scroll">
        <table className="cro-table cro-opportunity-table">
          <thead>
            <tr className="cro-group-header">
              <th rowSpan={3} className="cro-actions-col">Acciones</th>
              <th rowSpan={3}>Año</th>
              <th colSpan={5}>Identificación oportunidades</th>
              <th colSpan={7}>Planificación de acciones</th>
            </tr>
            <tr className="cro-group-header cro-subgroup-header">
              <th rowSpan={2}>Nº</th>
              <th rowSpan={2}>Origen</th>
              <th rowSpan={2}>Oportunidades</th>
              <th rowSpan={2}>Procesos afectados</th>
              <th rowSpan={2}>Viabilidad oportunidad</th>
              <th rowSpan={2}>Controles/acciones a implantar</th>
              <th rowSpan={2}>Responsable</th>
              <th rowSpan={2}>Plazos</th>
              <th rowSpan={2} className="cro-separator-right">Recursos</th>
              <th colSpan={3} className="cro-efficacy-group">Eficacia</th>
            </tr>
            <tr className="cro-column-header">
              <th className="cro-separator-left">1er semestre</th>
              <th>2º semestre</th>
              <th>Resultado de la eficacia</th>
            </tr>
          </thead>
          <tbody>
            {loading || rows.length === 0 ? <EmptyOrLoading loading={loading} colSpan={14} emptyText="No hay oportunidades registradas." /> : rows.map((row) => (
              <tr key={row.id}>
                <td className="cro-row-actions">
                  <div className="cro-row-actions-inner">
                    <button type="button" title="Editar" onClick={() => onEdit(row)}><Edit3 size={15} /></button>
                    <button type="button" title="Eliminar" onClick={() => onDelete(row)}><Trash2 size={15} /></button>
                  </div>
                </td>
                <td><ScrollableTableValue>{row.anio}</ScrollableTableValue></td>
                <td><ScrollableTableValue emphasis>{row.numero}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.origen}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.oportunidades}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.procesos_afectados}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.viabilidad_oportunidad}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.controles_acciones_implantar}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.responsable}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.plazos}</ScrollableTableValue></td>
                <td className="cro-separator-right"><ScrollableTableValue>{row.recursos}</ScrollableTableValue></td>
                <td className="cro-separator-left"><ScrollableTableValue>{row.eficacia_primer_semestre}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.eficacia_segundo_semestre}</ScrollableTableValue></td>
                <td><ScrollableTableValue>{row.resultado_eficacia}</ScrollableTableValue></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RegistroModal({ state, setState, submitting, onSubmit }: { state: Extract<FormState, { open: true }>; setState: (state: FormState) => void; submitting: boolean; onSubmit: () => void }) {
  const update = (patch: Record<string, string | number | null>) => setState({ ...state, row: { ...state.row, ...patch } } as Extract<FormState, { open: true }>);
  return (
    <div className="cro-modal-backdrop">
      <div className="cro-modal">
        <div className="cro-modal-header">
          <div>
            <p className="cro-card-kicker">{state.tipo === "riesgo" ? "Riesgo" : "Oportunidad"}</p>
            <h2>{state.mode === "create" ? "Nuevo registro" : "Editar registro"}</h2>
          </div>
        </div>
        <div className="cro-form-grid">
          <label>Año<input type="number" value={state.row.anio} onChange={(e) => update({ anio: Number(e.target.value) })} /></label>
          <label>Nº<input value={state.row.numero} onChange={(e) => update({ numero: e.target.value })} /></label>
          <label>Origen<input value={state.row.origen ?? ""} onChange={(e) => update({ origen: e.target.value })} /></label>
          <label>Responsable<input value={state.row.responsable ?? ""} onChange={(e) => update({ responsable: e.target.value })} /></label>
        </div>
        {state.tipo === "riesgo" ? <RiesgoFields row={state.row as CalidadRiesgoPayload} update={update} /> : <OportunidadFields row={state.row as CalidadOportunidadPayload} update={update} />}
        <div className="cro-modal-actions">
          <button type="button" className="cro-button cro-button-muted" onClick={() => setState({ open: false })}>Cancelar</button>
          <button type="button" className="cro-button cro-button-primary" disabled={submitting} onClick={onSubmit}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

function RiesgoFields({ row, update }: { row: CalidadRiesgoPayload; update: (patch: Record<string, string | null>) => void }) {
  return (
    <div className="cro-form-grid cro-form-grid-wide">
      <label>Cuestiones<textarea value={row.cuestiones ?? ""} onChange={(e) => update({ cuestiones: e.target.value })} /></label>
      <label>Riesgos<textarea value={row.riesgos ?? ""} onChange={(e) => update({ riesgos: e.target.value })} /></label>
      <label>Procesos afectados<textarea value={row.procesos_afectados ?? ""} onChange={(e) => update({ procesos_afectados: e.target.value })} /></label>
      <label>Decisión de la dirección<textarea value={row.decision_direccion ?? ""} onChange={(e) => update({ decision_direccion: e.target.value })} /></label>
      <label>Controles/acciones a implantar<textarea value={row.controles_acciones_implantar ?? ""} onChange={(e) => update({ controles_acciones_implantar: e.target.value })} /></label>
      <label>Plazos<textarea value={row.plazos ?? ""} onChange={(e) => update({ plazos: e.target.value })} /></label>
      <label>Recursos<textarea value={row.recursos ?? ""} onChange={(e) => update({ recursos: e.target.value })} /></label>
      <label>Eficacia 1er semestre<textarea value={row.eficacia_primer_semestre ?? ""} onChange={(e) => update({ eficacia_primer_semestre: e.target.value })} /></label>
      <label>Eficacia 2º semestre<textarea value={row.eficacia_segundo_semestre ?? ""} onChange={(e) => update({ eficacia_segundo_semestre: e.target.value })} /></label>
      <label>Resultado de la eficacia<textarea value={row.resultado_eficacia ?? ""} onChange={(e) => update({ resultado_eficacia: e.target.value })} /></label>
    </div>
  );
}

function OportunidadFields({ row, update }: { row: CalidadOportunidadPayload; update: (patch: Record<string, string | null>) => void }) {
  return (
    <div className="cro-form-grid cro-form-grid-wide">
      <label>Oportunidades<textarea value={row.oportunidades ?? ""} onChange={(e) => update({ oportunidades: e.target.value })} /></label>
      <label>Procesos afectados<textarea value={row.procesos_afectados ?? ""} onChange={(e) => update({ procesos_afectados: e.target.value })} /></label>
      <label>Viabilidad oportunidad<textarea value={row.viabilidad_oportunidad ?? ""} onChange={(e) => update({ viabilidad_oportunidad: e.target.value })} /></label>
      <label>Controles/acciones a implantar<textarea value={row.controles_acciones_implantar ?? ""} onChange={(e) => update({ controles_acciones_implantar: e.target.value })} /></label>
      <label>Plazos<textarea value={row.plazos ?? ""} onChange={(e) => update({ plazos: e.target.value })} /></label>
      <label>Recursos<textarea value={row.recursos ?? ""} onChange={(e) => update({ recursos: e.target.value })} /></label>
      <label>Eficacia 1er semestre<textarea value={row.eficacia_primer_semestre ?? ""} onChange={(e) => update({ eficacia_primer_semestre: e.target.value })} /></label>
      <label>Eficacia 2º semestre<textarea value={row.eficacia_segundo_semestre ?? ""} onChange={(e) => update({ eficacia_segundo_semestre: e.target.value })} /></label>
      <label>Resultado de la eficacia<textarea value={row.resultado_eficacia ?? ""} onChange={(e) => update({ resultado_eficacia: e.target.value })} /></label>
    </div>
  );
}

function ControlModal({ state, setState, submitting, onSubmit }: { state: Extract<ControlState, { open: true }>; setState: (state: ControlState) => void; submitting: boolean; onSubmit: () => void }) {
  const update = (patch: Partial<CalidadControlModificacionPayload>) => setState({ ...state, row: { ...state.row, ...patch } });
  return (
    <div className="cro-modal-backdrop">
      <div className="cro-modal cro-modal-small">
        <div className="cro-modal-header"><h2>{state.mode === "create" ? "Nueva" : "Editar"} modificación</h2></div>
        <div className="cro-form-grid cro-form-grid-single">
          <label>Fecha<input value={state.row.fecha} onChange={(e) => update({ fecha: e.target.value })} /></label>
          <label>Modificaciones realizadas<textarea value={state.row.modificaciones_realizadas} onChange={(e) => update({ modificaciones_realizadas: e.target.value })} /></label>
        </div>
        <div className="cro-modal-actions">
          <button type="button" className="cro-button cro-button-muted" onClick={() => setState({ open: false })}>Cancelar</button>
          <button type="button" className="cro-button cro-button-primary" disabled={submitting} onClick={onSubmit}>Guardar</button>
        </div>
      </div>
    </div>
  );
}


function PlantillaModal({ years, sourceYear, targetYear, submitting, onClose, onSubmit }: { years: number[]; sourceYear: number | null; targetYear: number; submitting: boolean; onClose: () => void; onSubmit: (payload: { source_year: number; target_year: number; copy_riesgos: boolean; copy_oportunidades: boolean }) => void }) {
  const [source, setSource] = useState(sourceYear ?? years[0] ?? new Date().getFullYear());
  const [target, setTarget] = useState(targetYear);
  const [copyRiesgos, setCopyRiesgos] = useState(true);
  const [copyOportunidades, setCopyOportunidades] = useState(true);
  return (
    <div className="cro-modal-backdrop">
      <div className="cro-modal cro-modal-small">
        <div className="cro-modal-header">
          <div>
            <p className="cro-card-kicker">Plantilla anual</p>
            <h2>Crear plantilla anual</h2>
          </div>
        </div>
        <p className="cro-modal-note">Duplica riesgos y oportunidades desde un año existente hacia un nuevo año. No copia el control documental.</p>
        <div className="cro-form-grid cro-form-grid-single">
          <label>Año origen<select value={source} onChange={(e) => setSource(Number(e.target.value))}>{years.map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
          <label>Año nuevo<input type="number" value={target} onChange={(e) => setTarget(Number(e.target.value))} /></label>
          <label className="cro-check"><input type="checkbox" checked={copyRiesgos} onChange={(e) => setCopyRiesgos(e.target.checked)} /> Copiar riesgos</label>
          <label className="cro-check"><input type="checkbox" checked={copyOportunidades} onChange={(e) => setCopyOportunidades(e.target.checked)} /> Copiar oportunidades</label>
        </div>
        <div className="cro-modal-actions">
          <button type="button" className="cro-button cro-button-muted" onClick={onClose}>Cancelar</button>
          <button type="button" className="cro-button cro-button-primary" disabled={submitting || source === target || (!copyRiesgos && !copyOportunidades)} onClick={() => onSubmit({ source_year: source, target_year: target, copy_riesgos: copyRiesgos, copy_oportunidades: copyOportunidades })}>Crear plantilla</button>
        </div>
      </div>
    </div>
  );
}
